import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ConsentEvidenceResult } from '@whinerp/client';
import { formatDateTime } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * What a person agreed to, and when (F-082).
 *
 * "Store and display the wording the person actually saw. A boolean is not
 * evidence in a regulatory conversation." So each row shows the words, the
 * source and the address - and a row made before the words were kept says
 * so, rather than showing nothing where evidence should be.
 */
export function ConsentEvidencePanel({ objectKey, recordId }: { objectKey: 'contact' | 'lead'; recordId: string }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const evidence = useQuery<ConsentEvidenceResult, ApiError>({
    queryKey: ['consent-evidence', objectKey, recordId],
    queryFn: () => api.consentEvidence({ path: { objectKey, recordId } }),
  });

  return (
    <Panel data-region="consent-evidence">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('consent.evidence')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('consent.evidenceBody')}</p>
        {evidence.isPending ? null : evidence.isError ? (
          <p className="text-dense text-danger-text">{errorText(evidence.error)}</p>
        ) : evidence.data.evidence.length === 0 ? (
          <p data-consent-evidence-none className="text-meta text-ink-meta">{t('consent.none')}</p>
        ) : (
          <ul className="flex flex-col">
            {evidence.data.evidence.map((row) => (
              <li key={row.consentId} data-consent-evidence={row.status} className="flex flex-col gap-1 border-t border-t-rule py-2 first:border-t-0">
                <p className="text-dense text-ink">
                  <span className="font-medium">{row.channel}</span>{' '}
                  {t(
                    row.status === 'opted-in'
                      ? 'consent.optedIn'
                      : row.status === 'opted-out'
                        ? 'consent.optedOut'
                        : row.status === 'pending'
                          ? 'consent.pending'
                          : 'consent.unknown',
                  )}
                  {' · '}
                  {formatDateTime(row.recordedAt, format)}
                </p>
                {row.wording ? (
                  <blockquote data-consent-wording className="border-s-2 border-s-rule ps-2 text-dense text-ink">
                    <span className="block text-meta text-ink-meta">{t('consent.wording')}</span>
                    {row.wording}
                  </blockquote>
                ) : (
                  <p className="text-meta text-ink-meta">{t('consent.noWording')}</p>
                )}
                <p className="text-meta text-ink-meta">
                  {t('consent.from', { values: { source: row.source } })}
                  {row.ip ? ` · ${t('consent.ip', { values: { ip: row.ip } })}` : ''}
                  {row.recordedBy ? ` · ${t('consent.recordedBy', { values: { name: row.recordedBy } })}` : ''}
                </p>
                {row.status === 'pending' && row.expiresAt ? (
                  <p className="text-meta text-warning-text">
                    {t('consent.expires', { values: { date: formatDateTime(row.expiresAt, format) } })}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
